import { MAX_PAYMENT_AMOUNT } from "@equidefi/constants/payments";
import { CurrencyFormatter, NumberFormatter } from "@equidefi/i18n";
import {
  calculateMaxSubscriptionAmount,
  formatSecurities,
  offeringHidesSecurityLabel,
  offeringInvestorMinimumUnits,
  offeringIsIPO,
  offeringIsRegS,
  offeringIsRule506c,
  offeringIsTypeRegA,
} from "@equidefi/shared";
import { formatPhoneNumber } from "@equidefi/shared/helpers/string";
import * as Yup from "yup";

export const formatIssuerContact = (issuer) => {
  const email = issuer?.email;
  const phone = formatPhoneNumber(issuer?.phone);
  return [email, phone].filter(Boolean).join(" or ");
};

const contactIssuerMessage = (issuer) =>
  `If you would like to invest ${CurrencyFormatter.format(
    MAX_PAYMENT_AMOUNT,
  )} or more, please contact the issuer at ${formatIssuerContact(issuer)}`;

export const subscriptionSchema = ({ offering, investments = [] }) => {
  const unitPrice = Number(offering?.unit_price) || 1;
  const minUnits = offeringInvestorMinimumUnits(offering);
  const minAmount = minUnits * unitPrice;
  const hideSecurityLabel = offeringHidesSecurityLabel(offering);

  const cannotInvestMessage = hideSecurityLabel
    ? "Based on your stated annual income and net worth, you can not invest in this offering."
    : "Based on your stated annual income and net worth, you can not purchase shares in this offering.";

  // For 506(c) and Reg S offerings, allow non-accredited investors to submit indication
  const isRule506c = offeringIsRule506c(offering);
  const isRegA = offeringIsTypeRegA(offering);
  const isRegS = offeringIsRegS(offering);
  const isIPO = offeringIsIPO(offering);

  const accreditationValidation =
    isRule506c || isRegS
      ? Yup.boolean().required("Please indicate if you are an accredited investor")
      : Yup.boolean().required().oneOf([true]);

  const standardSchema = Yup.object({
    accredited: accreditationValidation,
    broker_dealer: Yup.boolean().required("Please indicate if you are affiliated with a broker-dealer"),
    quantity: Yup.number()
      .test({
        message: "Quantity is required",
        test: (quantity) => quantity != null,
      })
      .test({
        message: "Quantity must not be a decimal",
        test: (quantity) => Number(quantity) === Math.round(quantity),
      })
      .test({
        message: hideSecurityLabel
          ? `Minimum ${CurrencyFormatter.format(minAmount)} investment required`
          : `Minimum ${formatSecurities({ quantity: minUnits, offering }).toString()} required`,
        test: (quantity) => quantity == null || quantity >= minUnits,
      })
      .lessThan(MAX_PAYMENT_AMOUNT / unitPrice, contactIssuerMessage(offering?.issuer)),
    amount: Yup.number()
      .test({
        message: "Amount is required",
        test: (amount) => amount != null,
      })
      .test({
        message: `Minimum ${CurrencyFormatter.format(minUnits * unitPrice)} investment required`,
        test: (value) => value == null || value >= minAmount,
      })
      .lessThan(MAX_PAYMENT_AMOUNT, contactIssuerMessage(offering?.issuer)),
    selectedBroker: Yup.object({
      label: Yup.string().nullable(),
      value: Yup.object().nullable(),
    }).nullable(),
  });

  const incomeSchema = Yup.object({
    accredited: Yup.boolean(),
    broker_dealer: Yup.boolean().required("Please indicate if you are affiliated with a broker-dealer"),
    annual_income: Yup.number("Must be a number").when(["accredited"], (accredited, schema) => {
      if (accredited === true || accredited === "true") {
        return schema.nullable();
      }
      return schema.required("Annual income is required").min(1, "Annual income must be greater than $0");
    }),

    net_worth: Yup.number().when(["accredited"], (accredited, schema) => {
      if (accredited === true || accredited === "true") {
        return schema.nullable();
      }
      return schema.required("Net worth is required").min(1, "Net worth must be greater than $0");
    }),
    quantity: Yup.number()
      .required("Quantity is required")
      .min(
        minUnits,
        hideSecurityLabel
          ? `Minimum ${CurrencyFormatter.format(minAmount)} investment required`
          : `Minimum ${NumberFormatter.format(minUnits)} shares required`,
      )
      .test({
        message: "Quantity must not be a decimal",
        test: (quantity) => Number(quantity) === Math.round(quantity),
      })
      .when(["accredited", "annual_income", "net_worth"], (accredited, annualIncome, netWorth, schema) => {
        if (accredited === true || accredited === "true") return schema;

        // IPO: collect income/net worth data but never block the investor from proceeding
        if (isIPO) return schema;

        const maxAmount = calculateMaxSubscriptionAmount({
          annualIncome,
          netWorth,
          investments,
        });

        const maxQuantity = Math.round(maxAmount / unitPrice);

        if (maxAmount >= MAX_PAYMENT_AMOUNT) {
          return schema.lessThan(MAX_PAYMENT_AMOUNT / unitPrice, contactIssuerMessage(offering?.issuer));
        }

        if (maxQuantity === 0) {
          return schema.max(maxQuantity, cannotInvestMessage);
        }

        if (maxQuantity < minUnits) {
          if (investments.length > 0) {
            return schema.max(
              maxQuantity,
              hideSecurityLabel
                ? `Based on your previous investments you may only invest up to ${CurrencyFormatter.format(
                    maxQuantity * unitPrice,
                  )} and the minimum investment amount for this offering is ${CurrencyFormatter.format(minAmount)}.`
                : `Based on your previous investments you may only purchase up to ${NumberFormatter.format(
                    maxQuantity,
                  )} shares and minimum shares amount for this offering is ${minUnits} shares.`,
            );
          }

          return schema.max(
            maxQuantity,
            hideSecurityLabel
              ? `Based on your stated annual income and net worth, you can not invest in this offering. The minimum investment amount for this offering is ${CurrencyFormatter.format(minAmount)}.`
              : `Based on your stated annual income and net worth, you can not invest in this offering. The minimum shares amount for this offering is ${minUnits} shares.`,
          );
        }

        return schema.max(
          maxQuantity,
          hideSecurityLabel
            ? `Based on your stated annual income and net worth, your investment amount must be ${CurrencyFormatter.format(
                maxQuantity * unitPrice,
              )} or less.`
            : `Based on your stated annual income and net worth, your number of shares must be ${NumberFormatter.format(
                maxQuantity,
              )} or less.`,
        );
      })
      .lessThan(MAX_PAYMENT_AMOUNT / unitPrice, contactIssuerMessage(offering?.issuer)),
    amount: Yup.number()
      .required("Investment amount is required")
      .min(minAmount, `Minimum ${CurrencyFormatter.format(minAmount)} investment required`)
      .when(["accredited", "annual_income", "net_worth"], (accredited, annualIncome, netWorth, schema) => {
        if (accredited === true || accredited === "true") return schema;

        // IPO: collect income/net worth data but never block the investor from proceeding
        if (isIPO) return schema;

        const maxAmount = calculateMaxSubscriptionAmount({
          annualIncome,
          netWorth,
          investments,
        });
        const maxAmountFormatted = CurrencyFormatter.format(maxAmount);

        if (maxAmount >= MAX_PAYMENT_AMOUNT) {
          return schema.lessThan(MAX_PAYMENT_AMOUNT, contactIssuerMessage(offering?.issuer));
        }

        if (maxAmount === 0) {
          return schema.max(maxAmount, cannotInvestMessage);
        }

        if (maxAmount / unitPrice < minUnits) {
          if (investments.length > 0) {
            return schema.max(
              maxAmount,
              `Based on your previous investments you may only invest up to ${maxAmountFormatted} in this offering.`,
            );
          }

          return schema.max(
            maxAmount,
            `Based on your stated annual income and net worth, you can not invest in this offering. The minimum investment amount for this offering is ${CurrencyFormatter.format(
              unitPrice * minUnits,
            )}.`,
          );
        }

        return schema.max(
          maxAmount,
          `Based on your stated annual income and net worth, you may invest up to ${maxAmountFormatted}.`,
        );
      })
      .lessThan(MAX_PAYMENT_AMOUNT, contactIssuerMessage(offering?.issuer)),
    selectedBroker: Yup.object({
      label: Yup.string().nullable(),
      value: Yup.object().nullable(),
    }).nullable(),
  });

  return isRegA || isIPO ? incomeSchema : standardSchema;
};
